/* eslint-disable eqeqeq */
import moment from 'moment'
import config from 'src/app.config.js'
import worklistService from 'src/services/worklist.service'
import searchService from 'src/services/search.service'
import entityService from 'src/services/entity.service'
import userService from 'src/services/user.service'
import localStorageService from 'src/services/storage.service'
import searchData from 'src/data/search'
import featureFlags from 'src/services/featureFlags.service'

const lockedColumns = [
  {
    name: '_score',
    label: 'Match',
    sortable: true,
  },
  {
    name: 'displayName',
    label: 'Name',
    sortable: true,
  },
  {
    name: 'profiled',
    label: '',
  },
  {
    name: 'favorite',
    label: '',
  },
]

const state = {
  env: 'public',
  facets: [],
  worklists: [],
  entitylists: [],
  selectedWorklistId: undefined,
  worklistError: null,
  sharedWorklist: {},
  lockedColumns: lockedColumns,
  currentQueryParts: [],
  dealModes: [],
  redactedQuery: false,
  currentQueryModes: [],
  currentResultview: {},
  numberOfResults: undefined,
  max_score: 1,
  favorites: [],
  favoritesLoaded: false,
  purchases: [],
  cartKey: 'cartItems',
  redactedColumnNames: [],
  allFacets: [],
}

const getters = {
  facetsAsOptions(state) {
    return state.facets.map(facet => ({
      text: facet.label,
      value: facet.name,
      level: facet.level,
      type: facet.uiType,
    }))
  },
  searchableFacets(state) {
    return state.facets.filter(facet => facet.searchable)
  },
  columnListingFacets(state) {
    return state.facets.filter(facet => facet.columnListing)
  },
  accessibleColumnListingFacets(state, getters) {
    return getters.columnListingFacets.filter(facet => !facet.level)
  },
  getFacetByName(state, getters) {
    return function(name) {
      return state.allFacets.find(facet => facet.name === name)
    }
  },
  dealOptions(state, getters) {
    const sigdevTypeFacet = getters.getFacetByName('subKind')
    if (sigdevTypeFacet) {
      return sigdevTypeFacet.choices.filter(choice => choice.includes('Deal -'))
    }
    return []
  },
  userWorklists(state) {
    return state.worklists.filter(worklist => worklist.context === 'user')
  },
  autosaveWorklist(state) {
    return state.worklists.find(
      worklist => worklist.context === 'system' && worklist.name === 'autosave'
    )
  },
  favoritesEntitylist(state) {
    return state.entitylists.find(
      entitylist =>
        entitylist.context === 'system' && entitylist.name === 'Favorites'
    )
  },
  selectedWorklist(state, getters) {
    return getters.userWorklists.find(
      worklist => worklist._id === state.selectedWorklistId
    )
  },
  getWorklistById(state, getters) {
    return function(id) {
      return getters.userWorklists.find(worklist => worklist._id === id)
    }
  },
  getEntityLink(state, getters) {
    return function(entity) {
      if (state.env === 'internal') {
        let link = '/'
        if (entity._kind === 'company') {
          link += 'companies/'
        } else if (entity._kind === 'person') {
          link += 'people/'
        } else {
          link += 'entities/' + entity._kind + '/'
        }
        return link + entity._id
      } else {
        let start = '/data/company/'
        if (featureFlags.isEnabled('newProfile')) {
          const prefix = searchData.searchRoutePrefix
          const kind = searchData.routeByKind[entity._kind] || 'company'
          start = `${prefix}${kind}/`
        }
        return (
          start +
          (entity.displayName || entity.commonName || '-')
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '') +
          '/' +
          entity._id
        )
      }
    }
  },
  isSearchPage(state, getters, routeState) {
    return state.env === 'internal' || routeState.route.path.includes('search')
  },
  isProfilePage(state, getters, routeState) {
    return (
      state.env === 'public' && routeState.route.path.includes('companyProfile')
    )
  },
  getAppUrl(state, getters) {
    return config.client
  },
  includedInFavorites(state, getters) {
    return function(entity) {
      return !!state.favorites.find(item => item._id === entity._id)
    }
  },
}

const mutations = {
  setFacets(state, payload) {
    state.facets = payload
  },
  setAllFacets(state, payload) {
    state.allFacets = payload
  },
  receiveWorklists(state, payload) {
    state.worklists = payload || []
    if (!_.isEmpty(state.sharedWorklist)) {
      state.worklists.push(state.sharedWorklist)
    }
  },
  receiveWorklist(state, payload) {
    state.sharedWorklist = payload
    state.worklists.push(payload)
    state.selectedWorklistId = payload._id
  },
  setWorklists(state, payload) {
    state.worklists = payload
    if (!_.isEmpty(state.sharedWorklist)) {
      state.worklists.push(state.sharedWorklist)
    }
  },
  updateWorklists(state, payload) {
    state.worklists = [
      ...state.worklists.filter(worklist => worklist._id !== payload._id),
      payload,
    ]
  },
  clearWorklists(state) {
    state.worklists = []
    state.selectedWorklistId = undefined
    state.sharedWorklist = {}
  },
  setEntitylists(state, payload) {
    state.entitylists = payload
  },
  setSelectedWorklistId(state, payload) {
    state.selectedWorklistId = payload.worklistId
  },
  setWorklistError(state, payload) {
    state.worklistError = payload
  },
  setRedactedQuery(state, payload) {
    state.redactedQuery = payload
  },
  setCurrentSearch(state, payload) {
    state.currentQueryParts = payload.queryParts
    state.currentQueryModes = payload.queryModes
    state.dealModes = payload.dealModes
    state.numberOfResults = payload.numberOfResults
    state.max_score = payload.max_score
    state.redactedColumnNames = payload.redactedPropertyNames
    state.currentResultview = payload.resultview
  },
  setFavorites(state, payload) {
    state.favorites = payload
    state.favoritesLoaded = true
  },
  setPurchases(state, payload) {
    state.purchases = payload
  },
  setFavoritesLoaded(state, payload) {
    state.favoritesLoaded = payload.state
  },
  updateWorklistAccess(state, payload) {
    const selected = state.worklists.find(
      worklist => worklist._id === payload.worklistId
    )
    if (selected) {
      selected.access = payload.access
    }
  },
}

// Helper function to convert facets from plain api response to necessary format
const convertFacets = (facetsData, fullSectors, isPublic, getAll) => {
  let facetOptions
  const BreakException = {}
  let facetOptionSeparator = false
  const convertedFacets = facetsData.map(facet => {
    if (!facet.level || getAll) {
      if (facet.choices && Array.isArray(facet.choices)) {
        facetOptions = []
        try {
          facet.choices.forEach(choice => {
            const choiceSplit = choice.split('|')
            if (choiceSplit[0]) {
              const facetOption = {
                name: choiceSplit[choiceSplit.length - 1],
                label: choiceSplit[0],
              }
              if (facetOptionSeparator) {
                facetOption.facetOptionSeparator = true
                facetOptionSeparator = false
              }
              facetOptions.push(facetOption)
            } else {
              facetOptionSeparator = true
              if (isPublic) {
                throw BreakException
              }
            }
          })
        } catch (e) {
          if (e !== BreakException) {
            throw e
          }
        }
        facet.options = facetOptions
      }

      if (facet.choicesProps) {
        facetOptions = []
        try {
          facet.choicesProps.forEach(choice => {
            const choiceSplit = choice.split('|')
            if (choiceSplit[0]) {
              const facetOption = {
                name: choiceSplit[choiceSplit.length - 1],
                label: choiceSplit[0],
              }
              if (facetOptionSeparator) {
                facetOption.facetOptionSeparator = true
                facetOptionSeparator = false
              }
              facetOptions.push(facetOption)
            } else {
              facetOptionSeparator = true
              if (isPublic) {
                throw BreakException
              }
            }
          })
        } catch (e) {
          if (e !== BreakException) {
            throw e
          }
        }
        facet.optionsProps = facetOptions
      }

      if (facet.choicesKind) {
        facetOptions = []
        try {
          facet.choicesKind.forEach(choice => {
            const choiceSplit = choice.split('|')
            if (choiceSplit[0]) {
              const facetOption = {
                name: choiceSplit[choiceSplit.length - 1],
                label: choiceSplit[0],
              }
              if (facetOptionSeparator) {
                facetOption.facetOptionSeparator = true
                facetOptionSeparator = false
              }
              facetOptions.push(facetOption)
            } else {
              facetOptionSeparator = true
              if (isPublic) {
                throw BreakException
              }
            }
          })
        } catch (e) {
          if (e !== BreakException) {
            throw e
          }
        }
        facet.optionsKind = facetOptions
      }

      if (facet.name === 'fullSectorA') {
        return _.assign(facet, {
          name: 'fullSector',
          label: 'Subsector',
          uiType: 'subsector',
          choices: fullSectors,
          verbs: [
            {
              name: 'contains',
              label: 'contains',
              widget: 'aInput',
            },
            {
              name: 'includes',
              label: 'includes',
              widget: 'subsectorPicker',
            },
          ],
          standardVerb: 'includes',
          renderColumn: function(entity) {
            const output = []
            ;[
              'fullSectorA',
              'fullSectorB',
              'fullSectorC',
              'fullSectorD',
              'fullSectorE',
              'fullSectorF',
            ].forEach(fullSector => {
              if (entity[fullSector]) {
                output.push(entity[fullSector])
              }
            })
            return output.join('<br/>')
          },
        })
      } else if (facet.name === '_dataLastUpdated') {
        return _.assign(facet, {
          renderColumn: function(entity) {
            return moment(entity._dataLastUpdated).format('ll')
          },
        })
      } else if (facet.uiType === 'divider') {
        return _.assign(facet, {
          verbs: [
            {
              name: 'not_validated_since',
              label: 'validation is older than',
              widget: 'aInput',
              widgetLabel: 'Number of days',
            },
            {
              name: 'validated_after',
              label: 'validation is newer than',
              widget: 'aInput',
              widgetLabel: 'Number of days',
            },
            {
              name: 'not_validated',
              label: 'has not been validated yet',
            },
          ],
          standardVerb: 'not_validated',
          renderColumn: function(entity) {
            let validatedAt
            if (entity['_invalidated-' + facet.name]) {
              return 'invalidated'
            } else {
              if (entity['_validated_at-' + facet.name]) {
                validatedAt = moment(entity['_validated_at-' + facet.name])
                return (
                  validatedAt.format('ll') + ' (' + validatedAt.fromNow() + ')'
                )
              } else {
                return 'not yet validated'
              }
            }
          },
          sortKey: '_validated_at-' + facet.name,
        })
      } else if (facet.uiType === 'issues') {
        return _.assign(facet, {
          verbs: [
            {
              name: 'issuesInclude',
              label: 'include',
              widget: 'facetValueIssue',
            },
          ],
          standardVerb: 'issuesInclude',
          renderColumn: entity => {
            if (!entity._issues) {
              return ''
            }
            return entity._issues
              .map(issue => {
                return (
                  '<span class="label ' +
                  issue.level +
                  '" style="cursor: help" title="' +
                  issue.description +
                  '">' +
                  (issue.property || 'whole record') +
                  '</span>'
                )
              })
              .join('')
          },
        })
      } else {
        facet = _.assign(facet, {
          renderColumn: entity => entity[facet.name],
        })

        if (
          [
            'display-name',
            'text',
            'textarea',
            'currency-annual-list',
            'date-scalable',
          ].includes(facet.uiType)
        ) {
          facet = _.assign(facet, {
            verbs: [
              {
                name: 'contains',
                label: 'contains',
                widget: 'aInput',
              },
              {
                name: 'anything',
                label: 'has any value',
                widget: null,
              },
              {
                name: 'unknown',
                label: 'is unknown',
                widget: null,
              },
            ],
            standardVerb: 'contains',
          })
        } else if (['radio', 'select'].includes(facet.uiType)) {
          facet = _.assign(facet, {
            verbs: [
              {
                name: 'includes',
                label: 'includes',
                widget: 'aSelect',
              },
              {
                name: 'anything',
                label: 'has any value',
                widget: null,
              },
              {
                name: 'unknown',
                label: 'is unknown',
                widget: null,
              },
            ],
            standardVerb: 'includes',
          })

          try {
            facetOptions = []
            facet.choices.forEach(choice => {
              const choiceSplit = choice.split('|')
              if (choiceSplit[0]) {
                const facetOption = {
                  name: choiceSplit[choiceSplit.length - 1],
                  label: choiceSplit[0],
                }
                if (facetOptionSeparator) {
                  facetOption.facetOptionSeparator = true
                  facetOptionSeparator = false
                }
                facetOptions.push(facetOption)
              } else {
                facetOptionSeparator = true
                if (isPublic) {
                  throw BreakException
                }
              }
            })
          } catch (e) {
            if (e !== BreakException) throw e
          }

          facet = _.assign(facet, {
            options: facetOptions || [],
          })
        } else if (facet.uiType === 'range') {
          facet = _.assign(facet, {
            verbs: [
              {
                name: 'gte',
                label: 'greater than or equal to',
                widget: 'aInput',
              },
            ],
            standardVerb: 'gte',
            renderColumn: entity => _.get(entity, facet.name, ''),
          })
        }

        if (isPublic) {
          if (facet.name === 'sector' || facet.name === 'baseCountry') {
            facet = _.assign(facet, {
              verbs: [
                {
                  name: 'includes',
                  label: 'includes',
                  widget: 'aSelect',
                },
              ],
            })
          }
          if (facet.name === 'displayName') {
            facet = _.assign(facet, {
              label: 'Name',
              verbs: [
                {
                  name: 'contains',
                  label: 'contains',
                  widget: 'aInput',
                },
              ],
            })
          }
          if (
            facet.name === 'employeeRange' ||
            facet.name === 'AnnualRevenue'
          ) {
            facet = _.assign(facet, {
              verbs: [
                {
                  name: 'includes',
                  label: 'between',
                  widget: 'aSelect',
                },
                {
                  name: 'unknown',
                  label: 'is unknown',
                  widget: null,
                },
              ],
            })
          }
        }

        return facet
      }
    } else {
      if (facet.name === 'fullSectorA') {
        return _.assign(facet, {
          name: 'fullSector',
          label: 'Subsector',
        })
      } else {
        return facet
      }
    }
  })
  return convertedFacets
}

const actions = {
  getFacets({ commit, state, rootGetters }) {
    const searchKind = rootGetters.searchKind
    return searchService.getSearch2Facets(searchKind).then(res => {
      const facets = res.data
      let facetData = facets.data
      const isPublic = state.env === 'public'
      const convertedAllFacets = convertFacets(
        facetData,
        facets.fullSectors,
        isPublic,
        true
      )
      const convertedNotInternalFacets = convertedAllFacets.filter(
        facet => facet.level !== 'internal'
      )
      commit('setFacets', convertedNotInternalFacets)
      commit('setAllFacets', convertedAllFacets)
    })
  },

  getWorklists(context, payload) {
    if (context.rootState.user.currentUser.userId) {
      return worklistService
        .getWorklists(context.rootState.user.currentUser.userId)
        .then(res => {
          const worklists = res.data
          context.commit('setWorklists', worklists.items)
          if (payload && payload.worklistId) {
            context.commit('setSelectedWorklistId', {
              worklistId: payload.worklistId,
            })
          }
        })
    } else {
      context.commit('setWorklists', [])
      return new Promise((resolve, reject) => {
        resolve([])
      })
    }
  },

  clearWorklists(context) {
    context.commit('clearWorklists')
  },

  getEntitylists(context) {
    if (context.getters.isLoggedIn && context.state.env === 'public') {
      return entityService
        .getEntitylists(context.rootState.user.currentUser.userId)
        .then(res => {
          const entitylists = res.data
          context.commit('setEntitylists', entitylists.items)
        })
    } else {
      context.commit('setEntitylists', [])
      return new Promise((resolve, reject) => {
        resolve([])
      })
    }
  },

  getFavorites(context) {
    context.commit('setFavoritesLoaded', {
      state: false,
    })
    if (context.getters.isLoggedIn && context.state.env === 'public') {
      context.dispatch('getEntitylists').then(() => {
        let idsToSearch
        if (context.getters.favoritesEntitylist) {
          idsToSearch = context.getters.favoritesEntitylist.include
            .filter(inc => !inc.archived)
            .map(inc => inc.entityId)
          if (idsToSearch.length > 0) {
            return searchService
              .postSearch2({
                kind: ['company', 'person'],
                count: idsToSearch.length,
                query: {
                  bool: {
                    must: [
                      {
                        terms: { _id: idsToSearch },
                      },
                    ],
                  },
                },
                sort: [
                  {
                    displayName: {
                      order: 'asc',
                    },
                  },
                ],
              })
              .then(function(res) {
                context.commit('setFavorites', res.data.data)
              })
          } else {
            context.commit('setFavorites', [])
          }
        } else {
          return entityService
            .postEntitylist(context.rootState.user.currentUser.userId, {
              name: 'Favorites',
              context: 'system',
              include: [],
            })
            .then(function() {
              return context.dispatch('getFavorites')
            })
        }
      })
    } else {
      context.commit(
        'setFavorites',
        localStorageService.get(context.state.cartKey) || []
      )
    }
  },

  getPurchases(context) {
    if (context.getters.isLoggedIn && context.state.env === 'public') {
      return userService
        .getTransactionsDataAcquiredWithAdditionalData(
          context.rootState.user.currentUser.userId
        )
        .then(purchases => {
          return searchService
            .postSearch2({
              query: {
                bool: {
                  must: [
                    {
                      terms: {
                        _id: purchases.map(purchase => purchase.entityId),
                      },
                    },
                  ],
                },
              },
            })
            .then(res => {
              context.commit(
                'setPurchases',
                res.data.data.map(purchase =>
                  _.assign(purchase, { purchased: true })
                )
              )
            })
        })
    } else {
      context.commit('setPurchases', [])
    }
  },

  autosave(context, payload) {
    if (context.getters.isLoggedIn && context.getters.autosaveWorklist) {
      worklistService.updateWorklist(context.getters.autosaveWorklist._id, {
        name: 'autosave',
        context: 'system',
        queryParts: payload.queryParts,
        queryModes: payload.queryModes,
        dealModes: payload.dealModes,
        searchKind: context.rootGetters.searchKind,
        columns: payload.columns,
        sorting: [payload.sorting],
      })
    }
  },

  saveWorklist(context, payload) {
    this._vm.$ga.event({
      eventCategory: 'Worklist',
      eventAction: 'Save',
      eventLabel: 'UserID ' + context.rootGetters.userId,
    })
    this._vm.$gtag.event('Save', {
      'event_category': 'Worklist',
      'event_label': 'UserID ' + context.rootGetters.userId,
    })
    return worklistService
      .postWorklist(context.rootState.user.currentUser.userId, {
        name: payload.name,
        context: 'user',
        queryParts: context.state.currentQueryParts,
        queryModes: context.state.currentQueryModes,
        dealModes: context.state.dealModes,
        searchKind: context.rootGetters.searchKind,
        columns: context.state.currentResultview.columns,
        sorting: [context.state.currentResultview.sorting],
      })
      .then(res => {
        context.dispatch('getWorklists', { worklistId: res.data._id })
        if (payload.cb) {
          payload.cb()
        }
      })
  },

  updateWorklist(context, payload) {
    this._vm.$ga.event({
      eventCategory: 'Worklist',
      eventAction: 'Update',
      eventLabel: 'UserID ' + context.rootGetters.userId,
    })
    this._vm.$gtag.event('Update', {
      'event_category': 'Worklist',
      'event_label': 'UserID ' + context.rootGetters.userId,
    })
    return worklistService
      .updateWorklist(payload.worklist._id, {
        name: payload.name,
        queryParts: context.state.currentQueryParts,
        queryModes: context.state.currentQueryModes,
        dealModes: context.state.dealModes,
        searchKind: context.rootGetters.searchKind,
        columns: context.state.currentResultview.columns,
        sorting: [context.state.currentResultview.sorting],
      })
      .then(res => {
        context.dispatch('getWorklists', { worklistId: payload.worklist._id })
        if (payload.cb) {
          payload.cb()
        }
      })
  },

  shareWorklist(context, payload) {
    this._vm.$ga.event({
      eventCategory: 'Worklist',
      eventAction: 'Share',
      eventLabel: 'UserID ' + context.rootGetters.userId,
    })
    this._vm.$gtag.event('Share', {
      'event_category': 'Worklist',
      'event_label': 'UserID ' + context.rootGetters.userId,
    })
    return worklistService
      .shareWorklist(payload.worklistId, {
        access: payload.access,
      })
      .then(() => {
        context.commit('updateWorklistAccess', payload)
      })
  },

  deleteWorklist(context, payload) {
    this._vm.$ga.event({
      eventCategory: 'Worklist',
      eventAction: 'Delete',
      eventLabel: 'UserID ' + context.rootGetters.userId,
    })
    this._vm.$gtag.event('Delete', {
      'event_category': 'Worklist',
      'event_label': 'UserID ' + context.rootGetters.userId,
    })
    return worklistService.deleteWorklist(payload.worklist._id).then(res => {
      context.commit('setSelectedWorklistId', {
        worklistId: undefined,
      })
      context.dispatch('getWorklists')
      if (payload.cb) {
        payload.cb()
      }
    })
  },

  createAutosaveWorklist(context) {
    if (context.getters.isLoggedIn) {
      return worklistService
        .postWorklist(context.rootState.user.currentUser.userId, {
          name: 'autosave',
          context: 'system',
          queryParts: [],
          queryModes: [],
          dealModes: [],
          searchKind: context.rootGetters.searchKind,
          columns: [],
          sorting: [
            {
              key: 'displayName',
              order: 'asc',
            },
          ],
        })
        .then(res => {
          context.dispatch('getWorklists')
        })
    }
  },

  addToFavorites(context, payload) {
    if (context.getters.isLoggedIn) {
      if (context.getters.favoritesEntitylist) {
        const favoritesEntitylist = context.getters.favoritesEntitylist
        const includeList = favoritesEntitylist.include
        payload.entities.forEach(entity => {
          const alreadyAddedItem = includeList.find(
            item => item.entityId === entity._id
          )
          if (alreadyAddedItem) {
            alreadyAddedItem.archived = false
            if (!alreadyAddedItem.reason.includes('added manually')) {
              alreadyAddedItem.reason.push('added manually')
            }
          } else {
            includeList.push({
              entityId: entity._id,
              reason: ['added manually'],
            })
          }
        })
        return entityService
          .patchEntitylist(favoritesEntitylist._id, {
            name: 'Favorites',
            context: 'system',
            include: includeList,
          })
          .then(res => {
            context.dispatch('getFavorites')
          })
      }
    } else {
      const favorites = context.state.favorites.slice()
      payload.entities.forEach(function(entity) {
        entityService.getEntityData(entity._id).then(res => {
          favorites.push(res.data)
          localStorageService.set(context.state.cartKey, favorites)
          context.dispatch('getFavorites')
        })
      })
    }
  },

  removeFromFavorites(context, payload) {
    if (context.getters.isLoggedIn) {
      if (context.getters.favoritesEntitylist) {
        const favoritesEntitylist = context.getters.favoritesEntitylist
        const includeList = favoritesEntitylist.include
        let isItemsToRemove = false
        payload.entities.forEach(entity => {
          const itemToRemove = includeList.find(
            item => item.entityId === entity._id
          )
          if (itemToRemove) {
            itemToRemove.archived = true
            isItemsToRemove = true
          }
        })

        if (isItemsToRemove) {
          return entityService
            .patchEntitylist(favoritesEntitylist._id, {
              name: 'Favorites',
              context: 'system',
              include: includeList,
            })
            .then(res => context.dispatch('getFavorites'))
        }
      }
    } else {
      payload.entities.forEach(entity => {
        const indexOfItemToRemove = context.state.favorites.findIndex(
          item => item._id === entity._id
        )
        if (indexOfItemToRemove > -1) {
          const favorites = context.state.favorites.slice()
          favorites.splice(indexOfItemToRemove, 1)
          localStorageService.set(context.state.cartKey, favorites)
          context.dispatch('getFavorites')
        }
      })
    }
  },

  fetchWorklistData(context, { worklistId }) {
    worklistService
      .getWorklistById(worklistId)
      .then(res => {
        const worklist = res.data
        context.commit('updateWorklists', worklist)
        context.commit('setSelectedWorklistId', {
          worklistId: worklist._id,
        })
      })
      .catch(err => {
        this._vm.$log(err)
        if (err.response && err.response.status) {
          context.commit('setWorklistError', err.response.status)
        }
      })
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
